import { Stack } from '@mui/material'
import { FC, useMemo } from 'react'
import type { FarmQuantificationUnit, ToGeoJSON, UnassignedPaddock } from '@/types/apiModels'
import { MonitoringPanel } from '../../../MonitoringPanel'
import { useStratificationContext } from '../../StratificationContext'
import { groupQUsByYear } from './helpers/groupQUsByYear'
import { groupUnassignedByCalendarYear } from './helpers/groupUnassignedByCalendarYear'
import { StratificationQUsBottomBanner } from './StratificationQUsBottomBanner'
import { StratificationQUsEmptyState } from './StratificationQUsEmptyState'
import { StratificationQUsSkeleton } from './StratificationQUsSkeleton'
import { StratificationQUsTopBanner } from './StratificationQUsTopBanner'
import { StratificationQUsYearGroup } from './StratificationQUsYearGroup'

const EMPTY_QUS: FarmQuantificationUnit[] = []
const EMPTY_UNASSIGNED: UnassignedPaddock[] = []
const EMPTY_AREAS: ToGeoJSON[] = []

export const StratificationQUs: FC = () => {
  const {
    samplingAreas,
    clusters,
    quantificationUnits,
    unassignedPaddocks,
    hasPlan,
    isLoadingQUs,
    hiddenSamplingAreaQUIds,
    toggleSamplingAreasVisibility,
  } = useStratificationContext()

  const clustersExist = (clusters?.length ?? 0) > 0
  const qusExist = (quantificationUnits?.length ?? 0) > 0

  const sortedQUsByYear = useMemo(
    () =>
      Object.entries(groupQUsByYear(quantificationUnits ?? EMPTY_QUS)).sort(
        ([a], [b]) => Number(a) - Number(b),
      ),
    [quantificationUnits],
  )
  const unassignedByYear = useMemo(
    () => groupUnassignedByCalendarYear(unassignedPaddocks ?? EMPTY_UNASSIGNED),
    [unassignedPaddocks],
  )

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <MonitoringPanel.Title>Listado de Unidades de cuantificación (QU)</MonitoringPanel.Title>

      {!clustersExist && (
        <MonitoringPanel.Description>
          Para visualizar los estratos, es necesario clusterizar y haber cargado información de Uso
          de los lotes que ingresan a programa de año de proyecto y año de línea de base (T=-1)
        </MonitoringPanel.Description>
      )}

      {clustersExist && (
        <>
          {!hasPlan && <StratificationQUsTopBanner />}
          {isLoadingQUs && <StratificationQUsSkeleton />}
          {!isLoadingQUs && !qusExist && <StratificationQUsEmptyState />}
          {!isLoadingQUs &&
            qusExist &&
            sortedQUsByYear.map(([year, qus]) => (
              <StratificationQUsYearGroup
                key={year}
                hiddenSamplingAreaQUIds={hiddenSamplingAreaQUIds}
                qus={qus}
                samplingAreas={samplingAreas ?? EMPTY_AREAS}
                year={year}
                onToggleSamplingAreasVisibility={toggleSamplingAreasVisibility}
              />
            ))}
          {Object.keys(unassignedByYear).length > 0 && (
            <StratificationQUsBottomBanner unassignedByYear={unassignedByYear} />
          )}
        </>
      )}
    </Stack>
  )
}
